<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <Table
      v-model:selected="selected"
      v-model:sort="sort"
      class="flex min-h-0 flex-1 flex-col"
    >
      <Table.Head :class="!rows.length && 'pointer-events-none'">
        <Table.Header primary field="name" sortable>Name</Table.Header>
        <Table.Header field="timestamp" sortable class="w-[270px]">
          Archived at
        </Table.Header>
        <Table.Header class="w-[190px]">Status</Table.Header>
      </Table.Head>

      <template v-if="loading && !rows.length">
        <Table.Row
          v-for="n in skeletonCount"
          :key="`skeleton-${n}`"
          :id="`skeleton-${n}`"
        >
          <template #media>
            <div class="flex w-9 items-center justify-center tablet-h:w-6">
              <Skeleton class="h-6 w-[17px]" />
            </div>
          </template>

          <Table.Cell primary>
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>
          <Table.Cell class="w-[270px]">
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>
          <Table.Cell class="w-[190px]">
            <Skeleton class="h-[13px] w-[85px]" />
          </Table.Cell>

          <template #mobile>
            <div class="flex w-full items-center pl-[10px] pr-[10px]">
              <div class="min-w-0 flex-1">
                <Skeleton class="mb-1 h-[13px] w-[85px]" />
                <Skeleton class="h-[13px] w-[150px]" />
              </div>
            </div>
          </template>
        </Table.Row>
      </template>

      <div
        v-else-if="!rows.length"
        class="grid min-h-0 flex-1 grid-rows-[45fr_auto_55fr] pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0 mr-4 tablet-h:mr-0"
      >
        <NoArchived class="row-start-2" />
      </div>

      <Table.Virtual
        v-else
        :rows="rows"
        :row-height="rowHeight"
        class="min-h-0 flex-1 pb-[calc(env(safe-area-inset-bottom)+100px)] tablet-h:pb-0"
        @row-click="
          (_event: MouseEvent, item: unknown) =>
            onRowClick(item as ArchivedFile)
        "
        @scroll-end="emit('scroll-end')"
      >
        <template #media-row="{ row }: { row: ArchivedFile }">
          <div class="w-9 tablet-h:w-6">
            <Thumbnail :type="thumbType(row)" :src="previewSrc(row)" />
          </div>
        </template>

        <template #row="{ row }: { row: ArchivedFile }">
          <Table.Cell primary>
            <span class="truncate">{{ row.fileName }}</span>
          </Table.Cell>

          <Table.Cell class="w-[270px]">
            <span v-if="!archivedTimestamp(row)" class="opacity-50">—</span>
            <span v-else>{{ archivedTimestamp(row) }}</span>
          </Table.Cell>

          <Table.Cell class="w-[190px]">{{ row.status || '—' }}</Table.Cell>
        </template>

        <template #mobile-row="{ row }: { row: ArchivedFile }">
          <div class="flex w-full items-center pl-[10px] pr-[10px]">
            <div class="min-w-0 flex-1">
              <p class="truncate text-[14px] leading-[18px] text-gray-800">
                {{ row.fileName }}
              </p>
              <div
                class="mt-1 flex items-center gap-[6px] text-[13px] leading-[18px] text-gray-600"
              >
                <span v-if="!archivedTimestamp(row)" class="opacity-50">
                  —
                </span>
                <span v-else class="truncate">{{
                  archivedTimestamp(row)
                }}</span>
              </div>
            </div>
            <button
              type="button"
              class="ml-[8px] mr-3 shrink-0 text-gray-800"
              @click.stop="onThreeDots(row)"
            >
              <MenuIcon class="size-[18px]" />
            </button>
          </div>
        </template>
      </Table.Virtual>
    </Table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  Table,
  Thumbnail,
  Skeleton,
  type ThumbnailType,
  type TableSort,
} from '@dacr/library'
import { MenuIcon } from '@dacr/icons'
import type { ArchivedFile } from '@/shared/graphql/legacy/generated'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useDevice } from '@/shared/composables/useDevice'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import NoArchived from './NoArchived.vue'

const props = defineProps<{
  items?: ArchivedFile[]
  loading?: boolean
}>()

const emit = defineEmits<{
  'scroll-end': []
}>()

const selected = defineModel<string[]>('selected', { default: () => [] })
const sort = defineModel<TableSort | null>('sort', { default: null })

const device = useDevice()

const rows = computed(() => props.items ?? [])

const rowHeight = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 42 : 65
)

const skeletonCount = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 4 : 7
)

const thumbType = (item: ArchivedFile): ThumbnailType =>
  fileTypeToThumbnail(item.type)

const previewSrc = (item: ArchivedFile) =>
  applyAuthorizationToken(item.imageUrl, [{ key: 'width', value: 36 }])

const archivedTimestamp = (item: ArchivedFile) =>
  formatTimestamp(item.archivedAt)

const onRowClick = (item: ArchivedFile) => {
  if (device.isMobile.value || device.isTabletVertical.value) return
  selected.value = selected.value[0] === item.id ? [] : [item.id]
}

const onThreeDots = (item: ArchivedFile) => {
  openSheet(Sheets.ArchiveMenu, { props: { item } })
}
</script>
